import { getSession, setActiveTenant, setSession } from '@sazeo/auth';
import { loadNamespace } from '@sazeo/i18n';
import type { TenantSettings } from '@sazeo/schemas';
import { DEMO_TENANT_SLUG } from '@sazeo/sdk';
import { type ComponentType, Suspense, lazy } from 'react';
import {
  type LoaderFunctionArgs,
  Navigate,
  RouterProvider,
  createBrowserRouter,
  redirect,
  useRouteLoaderData,
} from 'react-router-dom';

import { AddonRouteGuard } from './addons/AddonRouteGuard';
import { AppShell } from './components/AppShell';
import { PermissionGuard } from './components/PermissionGuard';
import { PermissionRoute } from './components/PermissionRoute';
import { DemoContext } from './lib/demoContext';
import { resolveTenantLogoUrl } from './lib/tenantBranding';
import {
  getTenantLoginRedirectUrl,
  getWorkspaceChooserUrl,
  resolveTenantFromHost,
} from './lib/tenantHost';
import {
  type MembershipSummary,
  type TenantRecord,
  type UserProfile,
  getCurrentMembership,
  getCurrentUserProfile,
  getTenantBySlug,
  isTenantMember,
} from './lib/tenantLoader';
import { NotFoundPage } from './pages/NotFoundPage';

const DevInspector = import.meta.env.DEV
  ? lazy(() => import('@sazeo/ui/dev').then((mod) => ({ default: mod.DevInspector })))
  : null;

export interface RootLoaderData {
  tenant: TenantRecord;
  user: UserProfile;
  membership: MembershipSummary | null;
  isDemo: boolean;
}

export interface AcceptInviteLoaderData {
  token: string | null;
  loginUrl: string | null;
  signedInEmail: string | null;
  isDev: boolean;
}

async function fetchDemoSession(): Promise<{
  access_token: string;
  refresh_token: string;
  expires_at: number;
} | null> {
  try {
    // Browser at *.app.sazeo.io has no proxy for /functions/v1 — the
    // Cloudflare Worker just serves the SPA. We must hit Supabase
    // directly via VITE_SUPABASE_URL. Same anon-key apikey header that
    // every Supabase Functions call needs.
    const supaUrl = import.meta.env?.VITE_SUPABASE_URL ?? '';
    const supaAnon = import.meta.env?.VITE_SUPABASE_ANON_KEY ?? '';
    if (!supaUrl) return null;
    const res = await fetch(`${supaUrl.replace(/\/+$/, '')}/functions/v1/demo-session`, {
      headers: supaAnon ? { apikey: supaAnon } : undefined,
    });
    if (!res.ok) return null;
    return (await res.json()) as {
      access_token: string;
      refresh_token: string;
      expires_at: number;
    };
  } catch {
    return null;
  }
}

function AppShellWithDemoContext() {
  const data = useRouteLoaderData('root') as RootLoaderData | null;
  return (
    <DemoContext.Provider value={data?.isDemo ?? false}>
      <AppShell />
    </DemoContext.Provider>
  );
}

function lazyPage<TModule, TKey extends keyof TModule & string>(
  load: () => Promise<TModule>,
  exportName: TKey,
) {
  return async () => {
    const mod = await load();
    const Component = mod[exportName] as unknown as ComponentType;
    return { Component };
  };
}

function lazyAddonPage<TModule, TKey extends keyof TModule & string>(
  addonId: string,
  load: () => Promise<TModule>,
  exportName: TKey,
) {
  return async () => {
    const mod = await load();
    const Page = mod[exportName] as unknown as ComponentType;

    function GuardedAddonPage() {
      return (
        <AddonRouteGuard addonId={addonId}>
          <Page />
        </AddonRouteGuard>
      );
    }

    return { Component: GuardedAddonPage };
  };
}

function lazyPermissionPage<TModule, TKey extends keyof TModule & string>(
  permission: string,
  load: () => Promise<TModule>,
  exportName: TKey,
) {
  return async () => {
    const mod = await load();
    const Page = mod[exportName] as unknown as ComponentType;

    function GuardedPermissionPage() {
      return (
        <PermissionGuard permission={permission}>
          <Page />
        </PermissionGuard>
      );
    }

    return { Component: GuardedPermissionPage };
  };
}

type Role = 'owner' | 'admin' | 'manager' | 'employee';

function lazyRoleGatedPage<TModule, TKey extends keyof TModule & string>(
  roles: Role[],
  load: () => Promise<TModule>,
  exportName: TKey,
) {
  return async () => {
    const mod = await load();
    const Page = mod[exportName] as unknown as ComponentType;

    function RoleGatedPage() {
      return (
        <PermissionRoute roles={roles}>
          <Page />
        </PermissionRoute>
      );
    }

    return { Component: RoleGatedPage };
  };
}

async function loadTenantLogoUrl(origin: string, accessToken: string): Promise<string | null> {
  try {
    const res = await fetch(new URL('/functions/v1/settings/tenant', origin), {
      headers: { authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) return null;
    const settings = (await res.json()) as Pick<TenantSettings, 'logo_url'>;
    return resolveTenantLogoUrl(settings.logo_url);
  } catch {
    return null;
  }
}

/**
 * Root loader — runs before any route renders.
 *
 * Order of operations (important — these are concurrency-sensitive):
 *   1. Resolve tenant slug from hostname / ?tenant override.
 *   2. Fetch session. If absent → bounce to app.sazeo.io/login.
 *   3. Resolve the tenant record for that slug. If missing → workspace chooser.
 *   4. Check membership. If not a member → workspace chooser.
 *   5. If the JWT's `active_tenant_id` ≠ resolved tenant, call
 *      `setActiveTenant(tenantId)` to refresh the claim, THEN bounce to a
 *      fresh load (`throw redirect(currentHref)`) so the next loader run
 *      sees the updated claim. Without the reload, the RLS context on
 *      subsequent Supabase queries would still reference the stale tenant.
 *   6. Load the user profile + any lazy-loaded namespaces, then return
 *      `{ tenant, user }`.
 */
async function rootLoader({ request }: LoaderFunctionArgs): Promise<RootLoaderData> {
  const url = new URL(request.url);
  const { slug, isDev } = resolveTenantFromHost(url.host, url.search);

  if (!slug) {
    // No slug on a tenant-app domain = user hit this app by mistake.
    throw redirect(getWorkspaceChooserUrl(isDev));
  }

  const isDemo = slug === DEMO_TENANT_SLUG;

  let session = await getSession();
  if (!session) {
    if (isDemo) {
      // Auto-login visitor for the demo tenant.
      const tokens = await fetchDemoSession();
      if (tokens) {
        await setSession({
          access_token: tokens.access_token,
          refresh_token: tokens.refresh_token,
        });
        session = await getSession();
      }
    }
    if (!session) {
      throw redirect(getTenantLoginRedirectUrl(url.href, isDev));
    }
  }

  const tenant = await getTenantBySlug(slug, { isDev });
  if (!tenant) {
    throw redirect(getWorkspaceChooserUrl(isDev));
  }

  const member = await isTenantMember(session.userId, tenant.id, { isDev });
  if (!member) {
    throw redirect(getWorkspaceChooserUrl(isDev));
  }

  // Claim freshness — if the JWT's active_tenant_id doesn't match the
  // subdomain the user is currently on, refresh and reload.
  if (session.activeTenantId !== tenant.id) {
    await setActiveTenant(tenant.id);
    throw redirect(url.pathname + url.search);
  }

  const [user, membership, tenantLogoUrl] = await Promise.all([
    getCurrentUserProfile(session.userId, session.email, { isDev }),
    getCurrentMembership(session.userId, tenant.id, { isDev }),
    loadTenantLogoUrl(url.origin, session.accessToken),
  ]);

  await Promise.allSettled([
    loadNamespace('core.common', user.preferred_lang),
    loadNamespace('core.nav', user.preferred_lang),
    loadNamespace('core.people', user.preferred_lang),
    loadNamespace('core.settings', user.preferred_lang),
    loadNamespace('core.documents', user.preferred_lang),
    loadNamespace('core.search', user.preferred_lang),
    loadNamespace('core.cmdK', user.preferred_lang),
    loadNamespace('core.home', user.preferred_lang),
    loadNamespace('core.feedback', user.preferred_lang),
    loadNamespace('core.invites', user.preferred_lang),
    loadNamespace('core.calendar', user.preferred_lang),
    loadNamespace('core.tasks', user.preferred_lang),
    loadNamespace('core.addons', user.preferred_lang),
    loadNamespace('addons.attendance', user.preferred_lang),
    loadNamespace('addons.crm-leads', user.preferred_lang),
    loadNamespace('addons.invoicing', user.preferred_lang),
    loadNamespace('addons.product-catalog', user.preferred_lang),
    loadNamespace('addons.expenses', user.preferred_lang),
    loadNamespace('finance.expenses', user.preferred_lang),
    loadNamespace('core.quotes', user.preferred_lang),
    loadNamespace('core.tickets', user.preferred_lang),
    loadNamespace('addons.vacations', user.preferred_lang),
    loadNamespace('addons.recruitment', user.preferred_lang),
    loadNamespace('addons.payroll', user.preferred_lang),
    loadNamespace('core.contacts', user.preferred_lang),
    loadNamespace('settings.developer', user.preferred_lang),
    loadNamespace('settings.rules', user.preferred_lang),
    loadNamespace('core.performance', user.preferred_lang),
    loadNamespace('hr.performance', user.preferred_lang),
    loadNamespace('core.schedules', user.preferred_lang),
    loadNamespace('core.notifications', user.preferred_lang),
    loadNamespace('core.reminders', user.preferred_lang),
    loadNamespace('core.org_chart', user.preferred_lang),
    loadNamespace('core.contracts', user.preferred_lang),
    loadNamespace('core.billing', user.preferred_lang),
    loadNamespace('addons.assets', user.preferred_lang),
    loadNamespace('addons.training', user.preferred_lang),
    loadNamespace('hr.orientation', user.preferred_lang),
    loadNamespace('core.announcements', user.preferred_lang),
    loadNamespace('addons.helpdesk', user.preferred_lang),
    loadNamespace('addons.projects', user.preferred_lang),
    loadNamespace('addons.suppliers', user.preferred_lang),
    loadNamespace('addons.goals', user.preferred_lang),
    loadNamespace('addons.company-events', user.preferred_lang),
    loadNamespace('addons.policies', user.preferred_lang),
    loadNamespace('addons.salary-requests', user.preferred_lang),
    loadNamespace('addons.time-tracking', user.preferred_lang),
    loadNamespace('addons.surveys', user.preferred_lang),
    loadNamespace('addons.budgets', user.preferred_lang),
    loadNamespace('addons.disciplinary', user.preferred_lang),
    loadNamespace('addons.benefits', user.preferred_lang),
    loadNamespace('addons.leave-management', user.preferred_lang),
    loadNamespace('addons.job-grades', user.preferred_lang),
    loadNamespace('addons.overtime-requests', user.preferred_lang),
    loadNamespace('addons.procurement', user.preferred_lang),
    loadNamespace('addons.wfh', user.preferred_lang),
    loadNamespace('addon.employee_schedules', user.preferred_lang),
    loadNamespace('core.reports', user.preferred_lang),
    loadNamespace('core.analytics', user.preferred_lang),
    loadNamespace('core.audit_log', user.preferred_lang),
    loadNamespace('core.cmdK', user.preferred_lang),
    loadNamespace('core.files', user.preferred_lang),
    loadNamespace('core.demo', user.preferred_lang),
    loadNamespace('workflows', user.preferred_lang),
    loadNamespace('settings.ai', user.preferred_lang),
  ]);

  // Swap the document title now that we know the tenant name.
  if (typeof document !== 'undefined') {
    document.title = `${tenant.name} · Sazeo`;
  }

  return {
    tenant: tenantLogoUrl ? { ...tenant, logo_url: tenantLogoUrl } : tenant,
    user,
    membership,
    isDemo,
  };
}

async function acceptInviteLoader({
  request,
}: LoaderFunctionArgs): Promise<AcceptInviteLoaderData> {
  const url = new URL(request.url);
  const { isDev } = resolveTenantFromHost(url.host, url.search);
  const session = await getSession();

  return {
    token: url.searchParams.get('token'),
    loginUrl: session ? null : getTenantLoginRedirectUrl(url.href, isDev),
    signedInEmail: session?.email ?? null,
    isDev,
  };
}

const router = createBrowserRouter([
  {
    path: '/demo-start',
    errorElement: <NotFoundPage />,
    lazy: lazyPage(() => import('./pages/DemoLandingPage'), 'DemoLandingPage'),
  },
  {
    path: '/apply/:slug',
    errorElement: <NotFoundPage />,
    lazy: lazyPage(() => import('./pages/public-apply/PublicApplyPage'), 'PublicApplyPage'),
  },
  {
    path: '/applications/:token/status',
    errorElement: <NotFoundPage />,
    lazy: lazyPage(
      () => import('./pages/public-apply/PublicApplicationStatusPage'),
      'PublicApplicationStatusPage',
    ),
  },
  {
    path: '/accept-invite',
    loader: acceptInviteLoader,
    errorElement: <NotFoundPage />,
    lazy: lazyPage(() => import('./pages/invitations/AcceptInvitePage'), 'AcceptInvitePage'),
  },
  // Dev-only standalone preview of the KAYA-parity TasksPage. Bypasses
  // rootLoader / AppShell so the visual structure can be reviewed locally
  // without auth, tenant context, or seeded data.
  {
    path: '/_preview/tasks',
    errorElement: <NotFoundPage />,
    lazy: lazyPage(() => import('./pages/tasks/TasksPreviewPage'), 'TasksPreviewPage'),
  },
  {
    id: 'root',
    loader: rootLoader,
    errorElement: <NotFoundPage />,
    element: <AppShellWithDemoContext />,
    children: [
      { index: true, element: <Navigate to="/home" replace /> },
      {
        path: 'home',
        lazy: lazyPage(() => import('./pages/home/HomePage'), 'HomePage'),
      },
      {
        path: 'people',
        lazy: lazyPage(() => import('./pages/people/PeoplePage'), 'PeoplePage'),
      },
      {
        path: 'org-chart',
        lazy: lazyPage(() => import('./pages/org-chart/OrgChartPage'), 'OrgChartPage'),
      },
      {
        path: 'people/teams',
        lazy: lazyPage(() => import('./pages/people/TeamsPage'), 'TeamsPage'),
      },
      {
        path: 'people/teams/:teamId',
        lazy: lazyPage(() => import('./pages/people/TeamDetailPage'), 'TeamDetailPage'),
      },
      {
        path: 'people/:userId',
        lazy: lazyPage(() => import('./pages/people/ProfilePage'), 'ProfilePage'),
      },

      {
        path: 'inbox',
        lazy: lazyPage(() => import('./pages/inbox/index'), 'InboxPage'),
      },
      {
        path: 'documents',
        lazy: lazyPage(() => import('./pages/documents/DocumentsPage'), 'DocumentsPage'),
      },
      {
        path: 'documents/:documentId/versions',
        lazy: lazyPage(
          () => import('./pages/documents/DocumentVersionsPage'),
          'DocumentVersionsPage',
        ),
      },
      {
        path: 'documents/signatures/new',
        lazy: lazyPage(
          () => import('./pages/documents/SignatureEnvelopePage'),
          'SignatureEnvelopePage',
        ),
      },
      {
        path: 'search',
        lazy: lazyPage(() => import('./pages/search/SearchPage'), 'SearchPage'),
      },
      {
        path: 'calendar',
        lazy: lazyPage(() => import('./pages/calendar/CalendarPage'), 'CalendarPage'),
      },
      {
        path: 'reminders',
        lazy: lazyPage(() => import('./pages/reminders/RemindersPage'), 'RemindersPage'),
      },
      {
        path: 'tasks',
        lazy: lazyPage(() => import('./pages/tasks/TasksPage'), 'TasksPage'),
      },
      {
        path: 'api-keys',
        lazy: lazyPage(() => import('./pages/api-keys/index'), 'ApiKeysPage'),
      },
      {
        path: 'messages',
        lazy: lazyPage(() => import('./pages/messages/index'), 'MessagesPage'),
      },
      {
        path: 'quotes',
        lazy: lazyPage(() => import('./pages/quotes/QuotesPage'), 'QuotesPage'),
      },
      {
        path: 'tickets',
        lazy: lazyPage(() => import('./pages/tickets/TicketsPage'), 'TicketsPage'),
      },
      {
        path: 'files',
        lazy: lazyPage(() => import('./pages/files/FilesPage'), 'FilesPage'),
      },

      {
        path: 'automations',
        lazy: lazyPage(() => import('./pages/automations/WorkflowsPage'), 'WorkflowsPage'),
      },
      {
        path: 'automations/runs',
        lazy: lazyPage(() => import('./pages/automations/WorkflowRunsPage'), 'WorkflowRunsPage'),
      },
      {
        path: 'automations/new',
        lazy: lazyPage(
          () => import('./pages/automations/WorkflowBuilderPage'),
          'WorkflowBuilderPage',
        ),
      },
      {
        path: 'automations/:workflowId',
        lazy: lazyPage(
          () => import('./pages/automations/WorkflowBuilderPage'),
          'WorkflowBuilderPage',
        ),
      },

      {
        path: 'attendance',
        lazy: lazyAddonPage(
          'attendance',
          () => import('@sazeo/addon-attendance'),
          'AttendancePage',
        ),
      },

      {
        path: 'vacations',
        lazy: lazyAddonPage(
          'vacations',
          () => import('./pages/vacations/VacationsPage'),
          'VacationsPage',
        ),
      },

      {
        path: 'expenses',
        lazy: () =>
          import('./pages/expenses/ExpenseReportsPage').then((m) => ({
            Component: m.ExpenseReportsPage,
          })),
      },
      {
        path: 'expenses/new',
        lazy: () =>
          import('./pages/expenses/ExpenseReportDetailPage').then((m) => ({
            Component: m.ExpenseReportDetailPage,
          })),
      },
      {
        path: 'expenses/:reportId',
        lazy: () =>
          import('./pages/expenses/ExpenseReportDetailPage').then((m) => ({
            Component: m.ExpenseReportDetailPage,
          })),
      },
      {
        path: 'expenses/:reportId/edit',
        lazy: () =>
          import('./pages/expenses/ExpenseReportDetailPage').then((m) => ({
            Component: m.ExpenseReportDetailPage,
          })),
      },
      {
        path: 'expenses/approvals',
        lazy: () =>
          import('./pages/expenses/ApprovalPage').then((m) => ({ Component: m.ApprovalPage })),
      },
      {
        path: 'expenses/reimbursements',
        lazy: () =>
          import('./pages/expenses/ReimbursementPage').then((m) => ({
            Component: m.ReimbursementPage,
          })),
      },

      {
        path: 'recruitment',
        lazy: lazyAddonPage(
          'recruitment',
          () => import('./pages/recruitment/RecruitmentPage'),
          'RecruitmentPage',
        ),
      },
      { path: 'hr', element: <Navigate to="/hr/vacancies" replace /> },
      {
        path: 'hr/vacancies',
        lazy: lazyAddonPage(
          'recruitment',
          () => import('./pages/recruitment/RecruitmentPage'),
          'RecruitmentPage',
        ),
      },
      {
        path: 'hr/vacancies/:vacancyId',
        lazy: lazyAddonPage(
          'recruitment',
          () => import('./pages/recruitment/VacancyPipelinePage'),
          'VacancyPipelinePage',
        ),
      },
      {
        path: 'hr/vacancies/:vacancyId/candidates',
        lazy: lazyAddonPage(
          'recruitment',
          () => import('./pages/recruitment/VacancyPipelinePage'),
          'VacancyPipelinePage',
        ),
      },

      {
        path: 'addons/contracts',
        lazy: lazyAddonPage(
          'contracts',
          () => import('./pages/contracts/ContractsPage'),
          'ContractsPage',
        ),
      },

      {
        path: 'addons/assets',
        lazy: lazyAddonPage(
          'asset-management',
          () => import('./pages/assets/AssetsPage'),
          'AssetsPage',
        ),
      },

      {
        path: 'addons/training',
        lazy: lazyAddonPage(
          'training',
          () => import('./pages/training/TrainingPage'),
          'TrainingPage',
        ),
      },

      {
        path: 'announcements',
        lazy: lazyPage(
          () => import('./pages/announcements/AnnouncementsPage'),
          'AnnouncementsPage',
        ),
      },

      {
        path: 'addons/helpdesk',
        lazy: lazyAddonPage(
          'helpdesk',
          () => import('./pages/helpdesk/HelpdeskPage'),
          'HelpdeskPage',
        ),
      },

      {
        path: 'addons/projects',
        lazy: lazyAddonPage(
          'projects',
          () => import('./pages/projects/ProjectsPage'),
          'ProjectsPage',
        ),
      },

      {
        path: 'addons/goals',
        lazy: lazyAddonPage('goals', () => import('./pages/goals/GoalsPage'), 'GoalsPage'),
      },

      {
        path: 'addons/company-events',
        lazy: lazyAddonPage(
          'company-events',
          () => import('./pages/company-events/CompanyEventsPage'),
          'CompanyEventsPage',
        ),
      },

      {
        path: 'addons/policies',
        lazy: lazyAddonPage(
          'policies',
          () => import('./pages/policies/PoliciesPage'),
          'PoliciesPage',
        ),
      },

      {
        path: 'addons/salary-requests',
        lazy: lazyAddonPage(
          'salary-requests',
          () => import('./pages/salary-requests/SalaryRequestsPage'),
          'SalaryRequestsPage',
        ),
      },

      {
        path: 'addons/disciplinary',
        lazy: lazyAddonPage(
          'disciplinary',
          () => import('./pages/disciplinary/DisciplinaryPage'),
          'DisciplinaryPage',
        ),
      },

      {
        path: 'payroll',
        lazy: lazyAddonPage('payroll', () => import('./pages/payroll/PayrollPage'), 'PayrollPage'),
      },

      {
        path: 'schedules',
        lazy: lazyAddonPage(
          'employee-schedules',
          () => import('./pages/schedules/SchedulesPage'),
          'SchedulesPage',
        ),
      },

      {
        path: 'schedules/manage',
        lazy: () =>
          import('./pages/schedules/ScheduleAdminPage').then((m) => ({
            Component: m.ScheduleAdminPage,
          })),
      },

      {
        path: 'performance',
        lazy: lazyAddonPage(
          'performance-reviews',
          () => import('./pages/performance/PerformanceReviewsPage'),
          'PerformanceReviewsPage',
        ),
      },

      {
        path: 'performance/manager',
        lazy: lazyPage(
          () => import('./pages/performance/PerformanceReviewPage'),
          'PerformanceReviewPage',
        ),
      },

      {
        path: 'performance/self',
        lazy: lazyPage(() => import('./pages/performance/SelfReviewPage'), 'SelfReviewPage'),
      },

      {
        path: 'addons/crm-leads',
        lazy: lazyAddonPage('crm-leads', () => import('@sazeo/addon-crm-leads'), 'CRMLeadsPage'),
      },
      {
        path: 'addons/crm-leads/:leadId',
        lazy: lazyAddonPage('crm-leads', () => import('@sazeo/addon-crm-leads'), 'LeadDetailPage'),
      },

      {
        path: 'addons/crm-contacts',
        lazy: lazyAddonPage(
          'crm-contacts',
          () => import('./pages/contacts/ContactsPage'),
          'ContactsPage',
        ),
      },

      {
        path: 'addons/crm-deduplication',
        lazy: lazyAddonPage(
          'crm-contacts',
          () => import('./pages/crm/DeduplicationPage'),
          'DeduplicationPage',
        ),
      },

      {
        path: 'addons/crm-lead-scoring',
        lazy: lazyAddonPage(
          'crm-leads',
          () => import('./pages/crm/LeadScoringDashboard'),
          'LeadScoringDashboard',
        ),
      },

      {
        path: 'addons/product-catalog',
        lazy: lazyAddonPage(
          'product-catalog',
          () => import('@sazeo/addon-product-catalog'),
          'ProductCatalogPage',
        ),
      },

      {
        path: 'addons/invoicing',
        lazy: lazyAddonPage('invoicing', () => import('@sazeo/addon-invoicing'), 'InvoicesPage'),
      },
      {
        path: 'addons/invoicing/customers',
        lazy: lazyAddonPage('invoicing', () => import('@sazeo/addon-invoicing'), 'CustomersPage'),
      },
      {
        path: 'addons/invoicing/customers/:customerId',
        lazy: lazyAddonPage(
          'invoicing',
          () => import('@sazeo/addon-invoicing'),
          'CustomerDetailPage',
        ),
      },
      {
        path: 'addons/invoicing/suppliers',
        lazy: lazyAddonPage('invoicing', () => import('@sazeo/addon-invoicing'), 'SuppliersPage'),
      },
      {
        path: 'addons/invoicing/:invoiceId',
        lazy: lazyAddonPage(
          'invoicing',
          () => import('@sazeo/addon-invoicing'),
          'InvoiceDetailPage',
        ),
      },

      {
        path: 'addons/time-tracking',
        lazy: lazyAddonPage(
          'time-tracking',
          () => import('./pages/time-tracking/TimeTrackingPage'),
          'TimeTrackingPage',
        ),
      },

      {
        path: 'addons/time-tracking/report',
        lazy: lazyAddonPage(
          'time-tracking',
          () => import('./pages/time-tracking/AttendanceReportPage'),
          'AttendanceReportPage',
        ),
      },

      {
        path: 'addons/surveys',
        lazy: lazyAddonPage('surveys', () => import('./pages/surveys/SurveysPage'), 'SurveysPage'),
      },

      {
        path: 'addons/budgets',
        lazy: lazyAddonPage('budgets', () => import('./pages/budgets/BudgetsPage'), 'BudgetsPage'),
      },

      {
        path: 'addons/benefits',
        lazy: lazyAddonPage(
          'benefits',
          () => import('./pages/benefits/BenefitsPage'),
          'BenefitsPage',
        ),
      },

      {
        path: 'addons/leave-management',
        lazy: lazyAddonPage(
          'leave-management',
          () => import('./pages/leave-management/LeaveManagementPage'),
          'LeaveManagementPage',
        ),
      },

      {
        path: 'addons/wfh',
        lazy: lazyAddonPage('wfh', () => import('./pages/wfh/WfhPage'), 'WfhPage'),
      },

      {
        path: 'addons/overtime-requests',
        lazy: lazyAddonPage(
          'overtime-requests',
          () => import('./pages/overtime/OvertimePage'),
          'OvertimePage',
        ),
      },

      {
        path: 'addons/procurement',
        lazy: lazyAddonPage(
          'procurement',
          () => import('./pages/procurement/ProcurementPage'),
          'ProcurementPage',
        ),
      },
      {
        path: 'procurement/requisitions',
        lazy: lazyAddonPage(
          'procurement',
          () => import('./pages/procurement/RequisitionsPage'),
          'RequisitionsPage',
        ),
      },
      {
        path: 'procurement/requisitions/:requisitionId',
        lazy: lazyAddonPage(
          'procurement',
          () => import('./pages/procurement/RequisitionDetailPage'),
          'RequisitionDetailPage',
        ),
      },
      {
        path: 'procurement/rfq',
        lazy: lazyAddonPage('procurement', () => import('./pages/procurement/RFQPage'), 'RFQPage'),
      },
      {
        path: 'procurement/rfq/:rfqId',
        lazy: lazyAddonPage(
          'procurement',
          () => import('./pages/procurement/RFQDetailPage'),
          'RFQDetailPage',
        ),
      },
      {
        path: 'procurement/orders',
        lazy: lazyAddonPage(
          'procurement',
          () => import('./pages/procurement/PurchaseOrderPage'),
          'PurchaseOrderPage',
        ),
      },
      {
        path: 'procurement/orders/:orderId',
        lazy: lazyAddonPage(
          'procurement',
          () => import('./pages/procurement/PODetailPage'),
          'PODetailPage',
        ),
      },
      {
        path: 'procurement/orders/:orderId/receive',
        lazy: lazyAddonPage(
          'procurement',
          () => import('./pages/procurement/ReceiptCreatePage'),
          'ReceiptCreatePage',
        ),
      },
      {
        path: 'procurement/receipts',
        lazy: lazyAddonPage(
          'procurement',
          () => import('./pages/procurement/ReceiptPage'),
          'ReceiptPage',
        ),
      },
      {
        path: 'procurement/receipts/:receiptId',
        lazy: lazyAddonPage(
          'procurement',
          () => import('./pages/procurement/ReceiptDetailPage'),
          'ReceiptDetailPage',
        ),
      },
      {
        path: 'procurement/vendors',
        lazy: lazyAddonPage(
          'procurement',
          () => import('./pages/procurement/VendorPage'),
          'VendorPage',
        ),
      },
      {
        path: 'procurement/vendors/:vendorId',
        lazy: lazyAddonPage(
          'procurement',
          () => import('./pages/procurement/VendorDetailPage'),
          'VendorDetailPage',
        ),
      },

      {
        path: 'procurement/requisitions/:requisitionId/edit',
        lazy: lazyAddonPage(
          'procurement',
          () => import('./pages/procurement/RequisitionEditPage'),
          'RequisitionEditPage',
        ),
      },
      {
        path: 'procurement/rfq/create',
        lazy: lazyAddonPage(
          'procurement',
          () => import('./pages/procurement/RFQCreatePage'),
          'RFQCreatePage',
        ),
      },
      {
        path: 'procurement/orders/create',
        lazy: lazyAddonPage(
          'procurement',
          () => import('./pages/procurement/PurchaseOrderCreatePage'),
          'PurchaseOrderCreatePage',
        ),
      },
      {
        path: 'procurement/orders/:orderId/edit',
        lazy: lazyAddonPage(
          'procurement',
          () => import('./pages/procurement/PurchaseOrderEditPage'),
          'PurchaseOrderEditPage',
        ),
      },
      {
        path: 'procurement/receipts/create',
        lazy: lazyAddonPage(
          'procurement',
          () => import('./pages/procurement/ReceiptCreatePage'),
          'ReceiptCreatePage',
        ),
      },
      {
        path: 'procurement/approvals',
        lazy: lazyAddonPage(
          'procurement',
          () => import('./pages/procurement/ApprovalQueuePage'),
          'ApprovalQueuePage',
        ),
      },

      {
        path: 'addons/job-grades',
        lazy: lazyAddonPage(
          'job-grades',
          () => import('./pages/job-grades/JobGradesPage'),
          'JobGradesPage',
        ),
      },

      {
        path: 'reports',
        children: [
          {
            index: true,
            lazy: lazyPage(() => import('./pages/reports/ReportsPage'), 'ReportsPage'),
          },
          {
            path: 'headcount',
            lazy: lazyPage(
              () => import('./pages/reports/HeadcountDetailPage'),
              'HeadcountDetailPage',
            ),
          },
          {
            path: 'payroll',
            lazy: lazyPage(() => import('./pages/reports/PayrollDetailPage'), 'PayrollDetailPage'),
          },
          {
            path: 'attendance',
            lazy: lazyPage(
              () => import('./pages/reports/AttendanceDetailPage'),
              'AttendanceDetailPage',
            ),
          },
          {
            path: 'turnover',
            lazy: lazyPage(
              () => import('./pages/reports/TurnoverDetailPage'),
              'TurnoverDetailPage',
            ),
          },
          {
            path: 'cohort',
            lazy: lazyPage(
              () => import('./pages/reports/CohortAnalysisPage'),
              'CohortAnalysisPage',
            ),
          },
          {
            path: 'funnel',
            lazy: lazyPage(
              () => import('./pages/reports/FunnelAnalysisPage'),
              'FunnelAnalysisPage',
            ),
          },
          {
            path: 'predictive',
            lazy: lazyPage(
              () => import('./pages/reports/PredictiveScoresPage'),
              'PredictiveScoresPage',
            ),
          },
        ],
      },

      {
        path: 'settings',
        children: [
          { index: true, element: <Navigate to="profile" replace /> },
          {
            path: 'profile',
            lazy: lazyPage(
              () => import('./pages/settings/ProfileSettingsPage'),
              'ProfileSettingsPage',
            ),
          },
          {
            path: 'data-export',
            lazy: lazyPage(
              () => import('./pages/settings/DataExportSettingsPage'),
              'DataExportSettingsPage',
            ),
          },
          {
            path: 'performance-reviews',
            lazy: lazyPage(
              () => import('./pages/settings/PerformanceReviewsAdminPage'),
              'PerformanceReviewsAdminPage',
            ),
          },
          {
            path: 'review-archive',
            lazy: lazyPage(() => import('./pages/settings/ReviewArchivePage'), 'ReviewArchivePage'),
          },
          {
            path: 'tenant',
            lazy: lazyRoleGatedPage(
              ['owner', 'admin'],
              () => import('./pages/settings/TenantSettingsPage'),
              'TenantSettingsPage',
            ),
          },
          {
            path: 'tenant/addons',
            lazy: lazyRoleGatedPage(
              ['owner', 'admin'],
              () => import('./pages/settings/AddonsSettingsPage'),
              'AddonsSettingsPage',
            ),
          },
          {
            path: 'billing',
            lazy: lazyRoleGatedPage(
              ['owner', 'admin'],
              () => import('./pages/settings/BillingSettings'),
              'BillingSettings',
            ),
          },
          {
            path: 'security',
            lazy: lazyPage(() => import('./pages/settings/SecuritySettings'), 'SecuritySettings'),
          },
          {
            path: 'mfa',
            lazy: lazyPage(() => import('./pages/settings/MfaSetupPage'), 'MfaSetupPage'),
          },
          {
            path: 'developer',
            lazy: lazyPermissionPage(
              'settings:api_keys:read',
              () => import('./pages/settings/DeveloperSettingsPage'),
              'DeveloperSettingsPage',
            ),
          },
          {
            path: 'integrations',
            lazy: lazyPermissionPage(
              'settings:api_keys:read',
              () => import('./pages/settings/IntegrationsSettingsPage'),
              'IntegrationsSettingsPage',
            ),
          },
          {
            path: 'audit',
            lazy: lazyRoleGatedPage(
              ['owner', 'admin'],
              () => import('./pages/settings/AuditLogPage'),
              'AuditLogPage',
            ),
          },
          {
            path: 'rules',
            lazy: lazyRoleGatedPage(
              ['owner', 'admin'],
              () => import('./pages/settings/RulesPage'),
              'RulesPage',
            ),
          },
          {
            path: 'ai',
            lazy: lazyPermissionPage(
              'settings:api_keys:read',
              () => import('./pages/settings/AiSettingsPage'),
              'AiSettingsPage',
            ),
          },
          {
            path: 'orientation-templates',
            lazy: lazyRoleGatedPage(
              ['owner', 'admin'],
              () => import('./pages/settings/OrientationTemplatesPage'),
              'OrientationTemplatesPage',
            ),
          },
          {
            path: 'roles',
            lazy: lazyRoleGatedPage(
              ['owner', 'admin'],
              () => import('./pages/settings/roles/RolesPage'),
              'RolesPage',
            ),
          },
          {
            path: 'roles/:roleId/ui-elements',
            lazy: lazyRoleGatedPage(
              ['owner', 'admin'],
              () => import('./pages/settings/roles/RoleUIElementsPage'),
              'RoleUIElementsPage',
            ),
          },
          {
            path: 'legacy',
            lazy: lazyPage(() => import('./pages/settings/SettingsPage'), 'SettingsPage'),
            children: [
              {
                index: true,
                lazy: lazyPage(() => import('./pages/settings/ProfileSettings'), 'ProfileSettings'),
              },
              {
                path: 'language',
                lazy: lazyPage(
                  () => import('./pages/settings/LanguageSettings'),
                  'LanguageSettings',
                ),
              },
              {
                path: 'theme',
                lazy: lazyPage(() => import('./pages/settings/ThemeSettings'), 'ThemeSettings'),
              },
            ],
          },
        ],
      },

      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

export function App() {
  return (
    <>
      <RouterProvider router={router} />
      {DevInspector ? (
        <Suspense fallback={null}>
          <DevInspector />
        </Suspense>
      ) : null}
    </>
  );
}
